import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Radio } from '@/components/Radio';
import Input from '../../../../components/Input';
import { Button } from '../../../../components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import usePostOrganizationNotificationCheck from '../api/usePostOrganizationNotificationCheck';
import type {
  Configuration,
  ConfigurationRequest,
  OrganizationResponse,
} from '../types/Organization';

interface NotificationSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (settings: ConfigurationRequest) => void;
  initialSettings?: Configuration;
  organization?: OrganizationResponse;
}

interface SlackSettingsFormProps {
  webhookUrl: string;
  onWebhookUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTestConnection: () => void;
  isTestingConnection: boolean;
  isConnectionVerified: boolean;
  errorMessage: string | null;
}

const SlackSettingsForm: React.FC<SlackSettingsFormProps> = ({
  webhookUrl,
  onWebhookUrlChange,
  onTestConnection,
  isTestingConnection,
  isConnectionVerified,
  errorMessage,
}) => {
  const isWebhookUrlValid = webhookUrl.trim().length > 0;

  return (
    <div className="mb-4">
      <div className="flex w-full min-w-[140px] items-center gap-4">
        <div className="flex shrink-0 items-center gap-4">
          <label className="text-sm font-bold">Slack Webhook URL</label>
          <button
            type="button"
            onClick={() =>
              window.open(
                'https://help.agenthub.jp/slack-223bd96c688541c9b2eeab45366464d6',
                '_blank'
              )
            }
            className="flex size-6 items-center justify-center rounded-full bg-primary text-sm font-bold text-white"
          >
            ?
          </button>
        </div>
        <div className="w-full">
          <Input
            className="w-full"
            value={webhookUrl}
            onChange={onWebhookUrlChange}
            placeholder="http://xxxxxxxxxx.com"
          />
          {errorMessage && <p className="mt-2 text-xs text-danger">{errorMessage}</p>}
        </div>
        {isConnectionVerified ? (
          <div className="flex shrink-0 items-center gap-2 font-bold text-primary">
            <MaterialIcon name="check" />
            <span className="text-sm">確認完了</span>
          </div>
        ) : (
          <Button
            size="md"
            color="secondary"
            onClick={onTestConnection}
            disabled={!isWebhookUrlValid}
          >
            {isTestingConnection ? (
              <MaterialIcon name="progress_activity" className="animate-spin opacity-50" />
            ) : (
              ''
            )}
            疎通確認
          </Button>
        )}
      </div>
    </div>
  );
};

interface ChatworkSettingsFormProps {
  apiToken: string;
  roomId: string;
  onApiTokenChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRoomIdChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTestConnection: () => void;
  isTestingConnection: boolean;
  isConnectionVerified: boolean;
  errorMessage: string | null;
}

const ChatworkSettingsForm: React.FC<ChatworkSettingsFormProps> = ({
  apiToken,
  roomId,
  onApiTokenChange,
  onRoomIdChange,
  onTestConnection,
  isTestingConnection,
  isConnectionVerified,
  errorMessage,
}) => {
  const isChatworkValid = apiToken.trim().length > 0 && roomId.trim().length > 0;

  return (
    <>
      <div className="mb-4 flex items-center">
        <div className="flex min-w-[140px] items-center gap-4">
          <div className="flex w-[190px] shrink-0 items-center gap-1">
            <label className="text-sm font-bold">Chatwork APIトークン</label>
            <button
              type="button"
              onClick={() =>
                window.open(
                  'https://help.agenthub.jp/chatwork-223bd96c688541c9b2eeab45366464d6',
                  '_blank'
                )
              }
              className="flex size-6 items-center justify-center rounded-full text-sm font-bold text-white"
            >
              ?
            </button>
          </div>
          <Input
            className="w-[380px]"
            value={apiToken}
            onChange={onApiTokenChange}
            placeholder="APIトークンを入力してください"
          />
        </div>
      </div>
      <div className="mb-4 flex items-center">
        <div className="flex min-w-[140px] items-center gap-4">
          <div className="w-[190px]">
            <label className="text-sm font-bold">Chatwork ルームID</label>
          </div>
          <Input
            className="w-[380px] shrink-0"
            value={roomId}
            onChange={onRoomIdChange}
            placeholder="ルームIDを入力してください"
          />
          {isConnectionVerified ? (
            <div className="flex shrink-0 items-center gap-2 font-bold text-primary">
              <MaterialIcon name="check" />
              <span className="text-sm">確認完了</span>
            </div>
          ) : (
            <Button
              size="md"
              color="secondary"
              onClick={onTestConnection}
              disabled={!isChatworkValid}
            >
              {isTestingConnection ? (
                <MaterialIcon name="progress_activity" className="animate-spin opacity-50" />
              ) : (
                ''
              )}
              疎通確認
            </Button>
          )}
        </div>
      </div>
      {errorMessage && <p className="mt-2 text-xs text-danger">{errorMessage}</p>}
    </>
  );
};

export default function NotificationSettingsModal({
  open,
  onClose,
  onSave,
  initialSettings = {
    notify_type: 'slack',
    configuration: {
      webhook_url: null,
      api_token: null,
      room_id: null,
    },
  },
  organization,
}: NotificationSettingsModalProps) {
  const [notificationType, setNotificationType] = useState<'slack' | 'chatwork'>(
    organization?.notification_setting?.configuration?.notify_type || 'slack'
  );
  const [webhookUrl, setWebhookUrl] = useState(
    initialSettings.configuration?.webhook_url ||
      organization?.notification_setting?.configuration?.slack?.webhook_url ||
      ''
  );
  const [apiToken, setApiToken] = useState(
    initialSettings.configuration?.api_token ||
      organization?.notification_setting?.configuration?.chatwork?.api_token ||
      ''
  );
  const [roomId, setRoomId] = useState(
    initialSettings.configuration?.room_id ||
      organization?.notification_setting?.configuration?.chatwork?.room_id ||
      ''
  );
  const [isTestingSlackConnection, setIsTestingSlackConnection] = useState(false);
  const [isTestingChatworkConnection, setIsTestingChatworkConnection] = useState(false);
  const [isSlackConnectionVerified, setIsSlackConnectionVerified] = useState(false);
  const [isChatworkConnectionVerified, setIsChatworkConnectionVerified] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutateAsync: checkNotification } = usePostOrganizationNotificationCheck();

  useEffect(() => {
    setNotificationType(organization?.notification_setting?.configuration?.notify_type || 'slack');
    setWebhookUrl(
      initialSettings.configuration?.webhook_url ||
        organization?.notification_setting?.configuration?.slack?.webhook_url ||
        ''
    );
    setApiToken(
      initialSettings.configuration?.api_token ||
        organization?.notification_setting?.configuration?.chatwork?.api_token ||
        ''
    );
    setRoomId(
      initialSettings.configuration?.room_id ||
        organization?.notification_setting?.configuration?.chatwork?.room_id ||
        ''
    );
  }, [initialSettings, organization]);

  const isFormValid =
    notificationType === 'slack'
      ? (webhookUrl ?? '').trim().length > 0 && isSlackConnectionVerified
      : (apiToken ?? '').trim().length > 0 &&
        (roomId ?? '').trim().length > 0 &&
        isChatworkConnectionVerified;

  const handleNotificationTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotificationType(e.target.value as 'slack' | 'chatwork');
    setErrorMessage(null);
  };

  const handleWebhookUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebhookUrl(e.target.value);
    setIsSlackConnectionVerified(false);
    setErrorMessage(null);
  };

  const handleApiTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiToken(e.target.value);
    setIsChatworkConnectionVerified(false);
    setErrorMessage(null);
  };

  const handleRoomIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRoomId(e.target.value);
    setIsChatworkConnectionVerified(false);
    setErrorMessage(null);
  };

  const handleTestSlackConnection = async () => {
    setIsTestingSlackConnection(true);
    setErrorMessage(null);
    try {
      await checkNotification({
        notify_type: 'slack',
        webhook_url: webhookUrl || '',
      });
      setIsSlackConnectionVerified(true);
    } catch (error) {
      console.error('Slack connection test failed:', error);
      setIsSlackConnectionVerified(false);
      setErrorMessage('＊疎通確認に失敗しました。入力したURLをご確認ください。');
    } finally {
      setIsTestingSlackConnection(false);
    }
  };

  const handleTestChatworkConnection = async () => {
    setIsTestingChatworkConnection(true);
    setErrorMessage(null);
    try {
      await checkNotification({
        notify_type: 'chatwork',
        chatwork_api_token: apiToken || '',
        chatwork_room_id: roomId || '',
      });
      setIsChatworkConnectionVerified(true);
    } catch (error) {
      console.error('Chatwork connection test failed:', error);
      setIsChatworkConnectionVerified(false);
      setErrorMessage('＊疎通確認に失敗しました。入力したAPIトークン, ルームIDをご確認ください。');
    } finally {
      setIsTestingChatworkConnection(false);
    }
  };

  const handleSave = async () => {
    if (notificationType === 'slack') {
      if (!webhookUrl.trim()) {
        setErrorMessage('＊Slack Webhook URLを入力し、疎通確認を行ってください。');
        return;
      }
      if (!isSlackConnectionVerified) {
        setErrorMessage('＊右のボタンから疎通確認を行ってください。');
        return;
      }
    } else {
      if (!apiToken.trim() || !roomId.trim()) {
        setErrorMessage('＊APIトークン, ルームIDを入力し、疎通確認を行なってください。');
        return;
      }
      if (!isChatworkConnectionVerified) {
        setErrorMessage('＊右のボタンから疎通確認を行ってください。');
        return;
      }
    }

    const settings: ConfigurationRequest = {
      notify_type: notificationType,
      webhook_url: notificationType === 'slack' ? webhookUrl : null,
      chatwork_api_token: notificationType === 'chatwork' ? apiToken : null,
      chatwork_room_id: notificationType === 'chatwork' ? roomId : null,
    };
    onSave(settings);
  };

  const handleClose = () => {
    // モーダルを閉じる際に元々の値に戻す
    setNotificationType(
      initialSettings.notify_type ||
        organization?.notification_setting?.configuration?.notify_type ||
        'slack'
    );
    setWebhookUrl(
      initialSettings.configuration?.webhook_url ||
        organization?.notification_setting?.configuration?.slack?.webhook_url ||
        ''
    );
    setApiToken(
      initialSettings.configuration?.api_token ||
        organization?.notification_setting?.configuration?.chatwork?.api_token ||
        ''
    );
    setRoomId(
      initialSettings.configuration?.room_id ||
        organization?.notification_setting?.configuration?.chatwork?.room_id ||
        ''
    );

    setIsSlackConnectionVerified(false);
    setIsChatworkConnectionVerified(false);
    setErrorMessage(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent className="w-full max-w-[800px] rounded-sm">
        <DialogTitle className="border-b border-solid border-gray-200 px-6 py-4 text-lg! font-bold!">
          通知先設定
        </DialogTitle>
        <div className="px-10 py-6">
          <div className="mb-6 flex gap-6">
            <Radio
              name="notificationType"
              value="slack"
              checked={notificationType === 'slack'}
              onChange={handleNotificationTypeChange}
            >
              Slack
            </Radio>
            <Radio
              name="notificationType"
              value="chatwork"
              checked={notificationType === 'chatwork'}
              onChange={handleNotificationTypeChange}
            >
              Chatwork
            </Radio>
          </div>

          {notificationType === 'slack' ? (
            <SlackSettingsForm
              webhookUrl={webhookUrl || ''}
              onWebhookUrlChange={handleWebhookUrlChange}
              onTestConnection={handleTestSlackConnection}
              isTestingConnection={isTestingSlackConnection}
              isConnectionVerified={isSlackConnectionVerified}
              errorMessage={errorMessage}
            />
          ) : (
            <ChatworkSettingsForm
              apiToken={apiToken || ''}
              roomId={roomId || ''}
              onApiTokenChange={handleApiTokenChange}
              onRoomIdChange={handleRoomIdChange}
              onTestConnection={handleTestChatworkConnection}
              isTestingConnection={isTestingChatworkConnection}
              isConnectionVerified={isChatworkConnectionVerified}
              errorMessage={errorMessage}
            />
          )}
        </div>

        <div className="flex justify-end gap-4 border-t border-solid border-gray-200 px-6 py-4">
          <Button color="secondary" size="md" onClick={handleClose}>
            キャンセル
          </Button>
          <Button color="primary" size="md" onClick={handleSave} disabled={!isFormValid}>
            設定する
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
